/* eslint react/no-unused-prop-types: 0 */
import { Icon, IconButton, Text, TextButton, TextLink } from '@oneaudi/unified-web-components';
import { useEffect, useRef, useState } from 'react';
import { useI18n } from '~/app/i18n/i18n';
import { Dialog } from '~/components/dialog';
import {
	CategoryDisclaimerStyles,
	GlobalDisclaimerBarStyles,
	GlobalDisclaimerLayerStyles,
} from '~/components/disclaimer/disclaimer-styles';
import { RenderHtml } from '~/components/text';
import { Container, InnerContainer } from '~/styles/container';

interface IndividualDisclaimerProps {
	content?: string;
	variant?: 'layer' | 'banner';
	headline?: string | null;
	link?: string;
	onLinkClick?: () => void;
}

export interface DisclaimerProps extends IndividualDisclaimerProps {
	type: 'global' | 'category' | 'general' | 'item' | 'audicode';
}

export const Disclaimer = ({ type, ...props }: DisclaimerProps) => {
	return {
		global: <GlobalDisclaimer {...props} />,
		category: <LocalDisclaimer {...{ ...props, type: 'category' }} />,
		general: <LocalDisclaimer {...{ ...props, type: 'general' }} />,
		item: <LocalDisclaimer {...{ ...props, type: 'item' }} />,
		audicode: <LocalDisclaimer {...{ ...props, type: 'audicode' }} />,
	}[type];
};

const GlobalDisclaimer = ({ variant, ...props }: IndividualDisclaimerProps) => {
	return variant
		? {
				layer: <GlobalDisclaimerLayer {...props} />,
				banner: <GlobalDisclaimerBar {...props} />,
			}[variant]
		: null;
};

const GlobalDisclaimerBar = ({ content }: { content?: string }) => {
	const [open, setOpen] = useState(true);
	return open ? (
		<GlobalDisclaimerBarStyles id={'global-disclaimer-bar'}>
			<Container>
				<InnerContainer>
					<Text tag="p" variant={{ type: 'copy', order: '1', style: 'normal' }}>
						<RenderHtml html={content} />
					</Text>
				</InnerContainer>
			</Container>
			<IconButton
				className="close-button"
				icon="cancel"
				size="md"
				variant={'tertiary'}
				onClick={() => setOpen(false)}
			/>
		</GlobalDisclaimerBarStyles>
	) : null;
};

const GlobalDisclaimerLayer = ({ content }: { content?: string }) => {
	const [open, setOpen] = useState(true);
	const { i18n } = useI18n();
	return (
		<Dialog open={open} onCloseButtonClick={() => setOpen(false)}>
			<GlobalDisclaimerLayerStyles>
				<Text tag="p" variant={{ type: 'copy', order: '1', style: 'normal' }}>
					<RenderHtml html={content} />
				</Text>
				<TextButton onClick={() => setOpen(false)} variant={'secondary'} size={'md'} className="okay-button">
					{i18n('ngc.actions.ok')}
				</TextButton>
			</GlobalDisclaimerLayerStyles>
		</Dialog>
	);
};

const LocalDisclaimer = ({ content, headline, type, link, onLinkClick }: DisclaimerProps) => {
	const { i18n } = useI18n();
	const [showMoreActive, setShowMoreActive] = useState(false);
	const [expanded, setExpanded] = useState(false);
	const [isClamped, setIsClamped] = useState(true);
	const [stopShowMoreCalculation, setStopShowMoreCalculation] = useState(false);

	const contentRef = useRef<HTMLDivElement>(null);

	// Remove empty p and span tags
	const filteredContent = content?.replace(/<(p|span)>\s*<\/\1>/g, '');

	const checkShowMore = () => {
		requestAnimationFrame(() => {
			if (contentRef.current && !stopShowMoreCalculation) {
				const { offsetHeight, scrollHeight } = contentRef.current;
				const _newShowMoreActive = offsetHeight < scrollHeight;
				setShowMoreActive(_newShowMoreActive);
				if (_newShowMoreActive) {
					setStopShowMoreCalculation(true);
				}
			}
		});
	};

	useEffect(() => {
		window.addEventListener('resize', () => {
			setStopShowMoreCalculation(false);
			checkShowMore();
		});
		return () => {
			window.removeEventListener('resize', () => {
				setStopShowMoreCalculation(false);
				checkShowMore();
			});
		};
	}, [setShowMoreActive, setStopShowMoreCalculation]);

	checkShowMore();

	return (
		<CategoryDisclaimerStyles $type={type} $expanded={expanded} $clamped={isClamped} tabIndex={0}>
			<div className="info">
				{['category', 'audicode'].includes(type) && <Icon className="icon" name="info-circle" size={'xs'} />}
				<Text tag="p" variant={{ type: 'copy', order: '2', style: 'bold' }}>
					{type === 'category' ? i18n('ngc.disclaimer.category.info') : headline}
				</Text>
			</div>
			<div className="disclaimer-content-wrapper">
				<Text
					ref={contentRef}
					tag="p"
					variant={{ type: 'copy', order: '2', style: 'normal' }}
					className="content">
					{<RenderHtml html={filteredContent} />}
				</Text>
			</div>
			{showMoreActive && (
				<TextLink
					size="sm"
					variant="standalone"
					onClick={(event) => {
						event.stopPropagation();
						setExpanded((prev) => !prev);
						expanded ? setTimeout(() => setIsClamped(true), 600) : setIsClamped(false);
					}}>
					{expanded ? i18n('ngc.actions.showLess') : i18n('ngc.actions.showMore')}
				</TextLink>
			)}
			{type === 'audicode' && (
				<div className="link">
					<Text variant={{ type: 'copy', order: '2', style: 'bold' }}>{i18n('ngc.audiCode.link')}:</Text>
					<TextLink
						size={'sm'}
						target={'_blank'}
						variant={'standalone'}
						href={link || '#'}
						iconPosition={'trailing'}
						icon={'external-link'}
						onClick={onLinkClick}>
						{link}
					</TextLink>
				</div>
			)}
		</CategoryDisclaimerStyles>
	);
};
